import { useState, useMemo } from 'react'
import { FiPlus, FiDownload, FiChevronLeft, FiChevronRight, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useCalendarioVacaciones } from '../../hooks/useCalendarioVacaciones'
import api from '../../services/api'
import { PageHeader, Tabs, Btn, Card, Chip, Pill, Avatar, SearchInput } from '../../ui'
import SolicitudAusenciaModal from '../../components/v2/SolicitudAusenciaModal'
import MiCalendarioMes from '../../components/v2/MiCalendarioMes'

/*
 * CalendarioVacaciones (V2) — rediseño según handoff_vacaciones.
 * Dos vistas conmutables: Mes (calendario tipo Linear con badges apilados)
 * y Equipo (Gantt mensual). Color por tipo de ausencia, pendientes con
 * borde discontinuo, festivos en celda con tinte rojo suave.
 */

// — Mapeo de tipos del backend al handoff. Color por tipo (no por estado).
const TIPO_TONE = {
  vacaciones:           { tone: 'emerald', color: '#10B981', label: 'Vacaciones' },
  baja_laboral:         { tone: 'purple',  color: '#7C3AED', label: 'Baja médica' },
  dia_libre:            { tone: 'brand',   color: '#2563EB', label: 'Día libre' },
  permiso_retribuido:   { tone: 'amber',   color: '#F59E0B', label: 'Permiso retribuido' },
  ausencia_justificada: { tone: 'neutral', color: '#64748B', label: 'Justificada' },
}
const TIPOS_KEYS = Object.keys(TIPO_TONE)

// — Festivos hardcodeados. TODO: parametrizar por CCAA + provincia del centro de trabajo.
const FESTIVOS = {
  // 2025
  '2025-01-01': 'Año nuevo',     '2025-01-06': 'Reyes',
  '2025-04-18': 'Viernes Santo', '2025-05-01': 'Día del Trabajo',
  '2025-08-15': 'Asunción',      '2025-10-12': 'Hispanidad',
  '2025-11-01': 'Todos los Santos', '2025-12-06': 'Constitución',
  '2025-12-08': 'Inmaculada',    '2025-12-25': 'Navidad',
  // 2026
  '2026-01-01': 'Año nuevo',     '2026-01-06': 'Reyes',
  '2026-04-03': 'Viernes Santo', '2026-04-06': 'Lunes de Pascua',
  '2026-05-01': 'Día del Trabajo','2026-05-02': 'Comunidad Madrid',
  '2026-08-15': 'Asunción',      '2026-10-12': 'Hispanidad',
  '2026-11-01': 'Todos los Santos','2026-12-06': 'Constitución',
  '2026-12-08': 'Inmaculada',    '2026-12-25': 'Navidad',
  // 2027
  '2027-01-01': 'Año nuevo',     '2027-01-06': 'Reyes',
  '2027-03-26': 'Viernes Santo', '2027-05-01': 'Día del Trabajo',
  '2027-08-15': 'Asunción',      '2027-10-12': 'Hispanidad',
  '2027-11-01': 'Todos los Santos','2027-12-06': 'Constitución',
  '2027-12-08': 'Inmaculada',    '2027-12-25': 'Navidad',
}

// — Utils —
const pad = (n) => String(n).padStart(2, '0')
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
const monthLabels = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                     'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

const monthGrid = (year, month) => {
  const first = new Date(year, month, 1)
  const startDow = (first.getDay() + 6) % 7 // 0=Lun, 6=Dom
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let i = startDow - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month, -i), inMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), inMonth: true })
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date
    const next = new Date(last); next.setDate(last.getDate() + 1)
    cells.push({ date: next, inMonth: false })
  }
  return cells
}

const formatMesYYYYMM = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`

const CalendarioVacacionesV2 = () => {
  const hoy = new Date()
  const [cursor, setCursor] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1))
  const [filter, setFilter] = useState('todos')
  const [estadoFiltro, setEstadoFiltro] = useState('todas')
  const [busqueda, setBusqueda] = useState('')
  const [selectedDay, setSelectedDay] = useState(null)
  const [view, setView] = useState('mes')
  const [modalSolicitud, setModalSolicitud] = useState(false)
  const [scope, setScope] = useState('equipo') // 'mi' | 'equipo' (Punto 4.B)

  const mesYYYYMM = formatMesYYYYMM(cursor)
  const filtros = useMemo(() => ({ estado: estadoFiltro }), [estadoFiltro])
  const { ausencias, cargando, recargar } = useCalendarioVacaciones(mesYYYYMM, filtros)

  // Filtrado por tipo + búsqueda. Recalculamos byDay localmente.
  const ausenciasFiltradas = useMemo(() => {
    return ausencias.filter((a) => {
      if (filter !== 'todos' && a.tipo !== filter) return false
      if (busqueda && !(a.empleado_nombre || '').toLowerCase().includes(busqueda.toLowerCase())) return false
      return true
    })
  }, [ausencias, filter, busqueda])

  const byDay = useMemo(() => {
    const map = {}
    ausenciasFiltradas.forEach((a) => {
      const from = parseISO(a.fecha_inicio), to = parseISO(a.fecha_fin)
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const k = ymd(d);
        (map[k] = map[k] || []).push(a)
      }
    })
    return map
  }, [ausenciasFiltradas])

  const cells = monthGrid(cursor.getFullYear(), cursor.getMonth())

  // Stats del mes (sin filtro de tipo, para resumen siempre completo)
  const empSet = new Set(ausencias.map((v) => v.empleado_id))
  const pending = ausencias.filter((v) => v.estado === 'pendiente').length

  const goPrev = () => { setSelectedDay(null); setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)) }
  const goNext = () => { setSelectedDay(null); setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)) }
  const goToday = () => { setSelectedDay(null); setCursor(new Date(hoy.getFullYear(), hoy.getMonth(), 1)) }

  const handleResponder = async (ausenciaId, accion) => {
    try {
      await api.post(`/ausencias/${ausenciaId}/responder`, { accion })
      toast.success(accion === 'aprobar' ? 'Ausencia aprobada' : 'Ausencia rechazada')
      await recargar()
      // Refrescar lista del día seleccionado tras la mutación
      if (selectedDay) {
        const lista = (byDay[selectedDay.fechaStr] || []).filter((a) => a.id !== ausenciaId)
        setSelectedDay({ ...selectedDay, events: lista })
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Error al procesar')
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        num="§ 04"
        kicker="Vacaciones"
        title={cursor.toLocaleDateString('es-ES', { month: 'long' })}
        italic={`${cursor.getFullYear()}`}
        sub={`${empSet.size} empleados con ausencia este mes · ${pending} pendientes de aprobar`}
        actions={
          <>
            <Tabs
              items={[{ value: 'mi', label: 'Mi calendario' }, { value: 'equipo', label: 'Equipo' }]}
              value={scope}
              onChange={(v) => { setScope(v); setSelectedDay(null) }}
            />
            {scope === 'equipo' && (
              <Tabs
                items={[{ value: 'mes', label: 'Mes' }, { value: 'equipo', label: 'Gantt' }]}
                value={view}
                onChange={(v) => { setView(v); setSelectedDay(null) }}
              />
            )}
            {scope === 'equipo' && (
              <Btn variant="ghost" size="sm" onClick={() => toast('Próximamente: exportar PDF/Excel')}>
                <FiDownload size={14} /> Exportar
              </Btn>
            )}
            {scope === 'equipo' && (
              <Btn variant="primary" size="sm" onClick={() => setModalSolicitud(true)}>
                <FiPlus size={14} /> Solicitar
              </Btn>
            )}
          </>
        }
      />

      {scope === 'mi' && (
        <div className="px-7 py-6">
          <MiCalendarioMes onChange={recargar} />
        </div>
      )}

      {scope === 'equipo' && (<>

      {/* Toolbar: filtros + búsqueda + navegación de mes */}
      <div className="px-7 py-5 flex items-center justify-between gap-4 border-b border-[var(--line)] flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip active={filter === 'todos'} onClick={() => setFilter('todos')} count={ausencias.length}>
            Todos
          </Chip>
          {TIPOS_KEYS.map((k) => {
            const v = TIPO_TONE[k]
            const n = ausencias.filter((x) => x.tipo === k).length
            return (
              <Chip key={k} active={filter === k} onClick={() => setFilter(k)} count={n}>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
                  {v.label}
                </span>
              </Chip>
            )
          })}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="h-9 px-3 bg-[var(--bg-2)] border border-[var(--line)] rounded-pill text-[12.5px] text-[var(--ink-2)]"
          >
            <option value="todas">Todas</option>
            <option value="aprobada">Solo aprobadas</option>
            <option value="pendiente">Solo pendientes</option>
            <option value="rechazada">Solo rechazadas</option>
          </select>
          <SearchInput
            placeholder="Empleado…"
            value={busqueda}
            onChange={setBusqueda}
            className="w-56"
          />
          <div className="flex items-center gap-1 border border-[var(--line)] rounded-pill px-1 h-9">
            <button onClick={goPrev}
                    className="p-1.5 rounded-full hover:bg-[var(--bg-2)] text-[var(--ink-3)]">
              <FiChevronLeft size={14} />
            </button>
            <button onClick={goToday}
                    className="px-3 h-7 rounded-full text-[12px] hover:bg-[var(--bg-2)] font-medium">
              Hoy
            </button>
            <button onClick={goNext}
                    className="p-1.5 rounded-full hover:bg-[var(--bg-2)] text-[var(--ink-3)]">
              <FiChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {view === 'mes' && (
        <div className="px-7 py-6 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-9">
            <CalendarGrid
              cells={cells}
              byDay={byDay}
              today={hoy}
              cursor={cursor}
              onSelectDay={(d) => setSelectedDay(d)}
              selectedKey={selectedDay?.fechaStr}
            />
            {cargando && <p className="text-[12px] text-[var(--ink-3)] mt-3">Cargando…</p>}
          </div>
          <div className="col-span-12 lg:col-span-3 flex flex-col gap-5">
            <SidePanel
              selectedDay={selectedDay}
              onClose={() => setSelectedDay(null)}
              monthEntries={ausencias}
              pending={pending}
              onResponder={handleResponder}
            />
          </div>
        </div>
      )}

      {view === 'equipo' && (
        <div className="px-7 py-6">
          <TeamTimeline cursor={cursor} ausencias={ausenciasFiltradas} today={hoy} />
          {cargando && <p className="text-[12px] text-[var(--ink-3)] mt-3">Cargando…</p>}
        </div>
      )}

      </>)}

      {modalSolicitud && (
        <SolicitudAusenciaModal
          onClose={() => setModalSolicitud(false)}
          onSuccess={() => { setModalSolicitud(false); recargar() }}
        />
      )}
    </div>
  )
}

// — Calendario tipo Linear/Notion: cuadrícula 7 columnas, celdas altas. —
const CalendarGrid = ({ cells, byDay, today, cursor, onSelectDay, selectedKey }) => {
  const dows = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM']
  return (
    <div className="ui-card overflow-hidden">
      <div className="grid grid-cols-7 border-b border-[var(--line)]">
        {dows.map((d, i) => (
          <div key={d} className={`px-3 py-2.5 eyebrow ${i >= 5 ? 'text-[var(--ink-4)]' : ''}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          const k = ymd(c.date)
          const events = byDay[k] || []
          const isToday = ymd(today) === k
          const isWeekend = c.date.getDay() === 0 || c.date.getDay() === 6
          const fest = FESTIVOS[k]
          return (
            <DayCell
              key={i}
              cell={c}
              keyStr={k}
              events={events}
              isToday={isToday}
              isWeekend={isWeekend}
              fest={fest}
              isSelected={selectedKey === k}
              onClick={() => onSelectDay({ date: c.date, fechaStr: k, events })}
            />
          )
        })}
      </div>
    </div>
  )
}

const MAX_VISIBLE = 3

const DayCell = ({ cell, keyStr, events, isToday, isWeekend, fest, isSelected, onClick }) => {
  const visible = events.slice(0, MAX_VISIBLE)
  const overflow = events.length - visible.length
  const dim = !cell.inMonth
  const colIdx = (cell.date.getDay() + 6) % 7
  const isLastCol = colIdx === 6
  return (
    <button
      onClick={onClick}
      className={`group text-left min-h-[124px] border-b border-[var(--line)] ${isLastCol ? '' : 'border-r'} px-2 pt-2 pb-1.5
        relative transition
        ${dim ? 'bg-[var(--bg-2)]/40' : ''}
        ${isWeekend && !dim ? 'bg-[var(--bg-2)]/60' : ''}
        ${fest ? 'bg-[rgba(239,68,68,0.04)]' : ''}
        ${isSelected ? 'ring-2 ring-inset ring-[var(--brand-1)]' : ''}
        hover:bg-[var(--bg-2)]`}
      aria-label={`${cell.date.getDate()} de ${monthLabels[cell.date.getMonth()]}, ${events.length} ausencia${events.length === 1 ? '' : 's'}`}
    >
      {/* Header: número + festivo / contador */}
      <div className="flex items-center justify-between mb-1.5">
        <span className={`inline-grid place-items-center text-[11.5px] font-mono tnum
          ${isToday ? 'w-6 h-6 rounded-full ia-grad-bg text-white font-semibold' : ''}
          ${dim ? 'text-[var(--ink-4)]' : fest ? 'text-danger' : 'text-[var(--ink-2)]'}`}>
          {cell.date.getDate()}
        </span>
        {fest && (
          <span className="text-[9.5px] font-mono uppercase tracking-wider text-danger truncate max-w-[68%]" title={fest}>
            {fest}
          </span>
        )}
        {!fest && events.length > 0 && (
          <span className="text-[9.5px] font-mono tnum text-[var(--ink-3)]">{events.length}</span>
        )}
      </div>

      {/* Stack de badges (máx 3) + chip overflow */}
      <div className="flex flex-col gap-1">
        {visible.map((ev, i) => <DayBadge key={`${ev.id}-${i}`} ev={ev} />)}
        {overflow > 0 && (
          <div className="relative" title={events.slice(MAX_VISIBLE).map((ev) => ev.empleado_nombre).join(', ')}>
            <div className="flex items-center gap-1 h-[18px] px-1 rounded-[5px] border border-dashed border-[var(--line-2)] text-[10.5px] text-[var(--ink-3)] font-mono">
              <AvatarStack ausencias={events.slice(MAX_VISIBLE)} size={14} max={3} />
              <span className="ml-0.5">+{overflow}</span>
            </div>
          </div>
        )}
      </div>
    </button>
  )
}

const DayBadge = ({ ev }) => {
  const t = TIPO_TONE[ev.tipo] || { color: '#64748B', label: ev.tipo }
  const pending = ev.estado === 'pendiente'
  const firstName = (ev.empleado_nombre || '').split(' ')[0]
  return (
    <div
      className="flex items-center gap-1.5 h-[20px] pl-0.5 pr-1.5 rounded-[6px] truncate"
      style={{
        background: pending ? 'transparent' : `${t.color}14`,
        border: pending ? `1px dashed ${t.color}66` : `1px solid ${t.color}22`,
      }}
      title={`${ev.empleado_nombre} · ${t.label}${pending ? ' · pendiente' : ''}`}
    >
      <Avatar name={ev.empleado_nombre || '?'} size={16} />
      <span className="text-[10.5px] truncate font-medium" style={{ color: t.color }}>
        {firstName}
      </span>
    </div>
  )
}

const AvatarStack = ({ ausencias, size = 18, max = 4 }) => {
  const visible = ausencias.slice(0, max)
  return (
    <span className="inline-flex items-center" style={{ paddingLeft: 4 }}>
      {visible.map((a, i) => (
        <span
          key={`${a.id}-${i}`}
          style={{
            marginLeft: i === 0 ? -4 : -6,
            zIndex: max - i,
            border: '1.5px solid var(--bg)',
            borderRadius: '999px',
            display: 'inline-block',
          }}
        >
          <Avatar name={a.empleado_nombre || '?'} size={size} />
        </span>
      ))}
    </span>
  )
}

// — Panel lateral (resumen sin selección, lista detallada con día) —
const SidePanel = ({ selectedDay, onClose, monthEntries, pending, onResponder }) => {
  if (!selectedDay) {
    return (
      <>
        <Card eyebrow="Selecciona" title="Detalle del día">
          <div className="text-[12.5px] text-[var(--ink-3)]">
            Pulsa cualquier día del mes para ver quién está fuera, el tipo de ausencia y aprobar
            o rechazar lo que esté pendiente.
          </div>
        </Card>
        <Card eyebrow="Resumen del mes" title={`${monthEntries.length} ausencias`}>
          <ul className="flex flex-col gap-2.5 text-[12.5px]">
            {TIPOS_KEYS.map((k) => {
              const tone = TIPO_TONE[k]
              const n = monthEntries.filter((v) => v.tipo === k).length
              return (
                <li key={k} className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: tone.color }} />
                  <span className="flex-1">{tone.label}</span>
                  <span className="font-mono tnum text-[var(--ink-3)]">{n}</span>
                </li>
              )
            })}
            <li className="flex items-center gap-2.5 pt-2 border-t border-[var(--line)] mt-1">
              <FiAlertCircle size={12} className="text-[#A86A06]" />
              <span className="flex-1">Pendientes de aprobar</span>
              <span className="font-mono tnum text-[#A86A06]">{pending}</span>
            </li>
          </ul>
        </Card>
      </>
    )
  }

  const { date, events } = selectedDay
  const dayLabel = date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
  return (
    <Card
      eyebrow={dayLabel}
      title={`${events.length} ${events.length === 1 ? 'ausencia' : 'ausencias'}`}
      action={
        <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" aria-label="Cerrar detalle">
          <FiX size={14} />
        </button>
      }
    >
      {events.length === 0 ? (
        <div className="text-[12.5px] text-[var(--ink-3)]">Nadie de vacaciones este día.</div>
      ) : (
        <ul className="flex flex-col divide-y divide-[var(--line)]">
          {events.map((ev) => {
            const t = TIPO_TONE[ev.tipo] || { tone: 'neutral', label: ev.tipo }
            const from = parseISO(ev.fecha_inicio), to = parseISO(ev.fecha_fin)
            const range = from.getTime() === to.getTime()
              ? `${from.getDate()} ${monthLabels[from.getMonth()].slice(0, 3)}`
              : `${from.getDate()} → ${to.getDate()} ${monthLabels[to.getMonth()].slice(0, 3)}`
            return (
              <li key={ev.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center gap-2.5">
                  <Avatar name={ev.empleado_nombre || '?'} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{ev.empleado_nombre}</div>
                    <div className="text-[11px] text-[var(--ink-3)] font-mono">{range}</div>
                  </div>
                  <Pill tone={t.tone}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                    {t.label}
                  </Pill>
                </div>
                {ev.estado === 'pendiente' && (
                  <div className="flex gap-1.5 mt-2.5">
                    <Btn variant="primary" size="sm" onClick={() => onResponder(ev.id, 'aprobar')}>
                      <FiCheck size={12} /> Aprobar
                    </Btn>
                    <Btn variant="ghost" size="sm" onClick={() => onResponder(ev.id, 'rechazar')}>
                      Rechazar
                    </Btn>
                  </div>
                )}
                {ev.estado === 'rechazada' && (
                  <div className="text-[10.5px] text-[var(--ink-3)] mt-1.5 italic">Rechazada</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// — Vista Equipo (Gantt mensual) —
const TeamTimeline = ({ cursor, ausencias, today }) => {
  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const days = new Date(year, month + 1, 0).getDate()

  // Agrupar ausencias por empleado y filtrar empleados sin segmentos en este mes.
  const empleados = useMemo(() => {
    const m = new Map()
    ausencias.forEach((a) => {
      if (!m.has(a.empleado_id)) {
        m.set(a.empleado_id, { id: a.empleado_id, nombre: a.empleado_nombre, grupo: a.grupo_nombre })
      }
    })
    return Array.from(m.values()).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
  }, [ausencias])

  const segmentsFor = (empId) => {
    const start = new Date(year, month, 1)
    const end = new Date(year, month, days)
    return ausencias
      .filter((v) => v.empleado_id === empId)
      .map((v) => {
        const from = parseISO(v.fecha_inicio), to = parseISO(v.fecha_fin)
        if (to < start || from > end) return null
        const s = from < start ? start : from
        const e = to > end ? end : to
        return { ...v, sDay: s.getDate(), eDay: e.getDate() }
      })
      .filter(Boolean)
  }

  if (empleados.length === 0) {
    return (
      <div className="ui-card p-8 text-center">
        <p className="text-[13px] text-[var(--ink-3)]">No hay ausencias en este mes con los filtros actuales.</p>
      </div>
    )
  }

  return (
    <div className="ui-card overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between">
        <div>
          <div className="eyebrow mb-1">Equipo</div>
          <div className="font-medium text-[14px] capitalize">
            Vista cronológica · {monthLabels[month]} {year}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {/* Header de días */}
        <div className="grid" style={{ gridTemplateColumns: `220px repeat(${days}, minmax(28px,1fr))`, minWidth: 220 + days * 28 }}>
          <div className="border-b border-r border-[var(--line)]" />
          {Array.from({ length: days }, (_, i) => {
            const d = new Date(year, month, i + 1)
            const isWE = d.getDay() === 0 || d.getDay() === 6
            const isFest = !!FESTIVOS[ymd(d)]
            const isToday = ymd(today) === ymd(d)
            return (
              <div
                key={i}
                className={`border-b border-[var(--line)] text-center py-1.5 text-[10px] font-mono
                  ${isFest ? 'text-danger' : isWE ? 'text-[var(--ink-4)]' : 'text-[var(--ink-3)]'}
                  ${isToday ? 'bg-[rgba(37,99,235,0.06)]' : ''}`}
              >
                <div>{['L', 'M', 'X', 'J', 'V', 'S', 'D'][(d.getDay() + 6) % 7]}</div>
                <div className={`tnum ${isToday ? 'text-[var(--ink)] font-semibold' : ''}`}>{i + 1}</div>
              </div>
            )
          })}
        </div>

        {/* Filas de empleados */}
        {empleados.map((e) => {
          const segs = segmentsFor(e.id)
          return (
            <div
              key={e.id}
              className="grid items-center"
              style={{ gridTemplateColumns: `220px repeat(${days}, minmax(28px,1fr))`, minWidth: 220 + days * 28 }}
            >
              <div className="px-4 py-2.5 border-b border-r border-[var(--line)] flex items-center gap-2.5">
                <Avatar name={e.nombre || '?'} size={22} />
                <div className="min-w-0">
                  <div className="text-[12.5px] truncate">{e.nombre}</div>
                  {e.grupo && <div className="text-[10px] text-[var(--ink-3)] truncate">{e.grupo}</div>}
                </div>
              </div>
              <div
                className="row-start-1 relative h-full"
                style={{ gridColumn: `2 / span ${days}` }}
              >
                {/* Fondo: cuadrícula de días */}
                <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days}, minmax(0,1fr))` }}>
                  {Array.from({ length: days }, (_, i) => {
                    const d = new Date(year, month, i + 1)
                    const isWE = d.getDay() === 0 || d.getDay() === 6
                    const isFest = !!FESTIVOS[ymd(d)]
                    const isToday = ymd(today) === ymd(d)
                    return (
                      <div
                        key={i}
                        className={`border-b border-[var(--line)] ${i < days - 1 ? 'border-r border-[var(--line)]' : ''}
                          ${isFest ? 'bg-[rgba(239,68,68,0.04)]' : isWE ? 'bg-[var(--bg-2)]/60' : ''}
                          ${isToday ? 'bg-[rgba(37,99,235,0.06)]' : ''}`}
                      />
                    )
                  })}
                </div>
                {/* Segmentos absolutos */}
                <div className="relative h-full">
                  {segs.map((s, i) => {
                    const t = TIPO_TONE[s.tipo] || { color: '#64748B', label: s.tipo }
                    const left = ((s.sDay - 1) / days) * 100
                    const width = ((s.eDay - s.sDay + 1) / days) * 100
                    const pendingFlag = s.estado === 'pendiente'
                    const range = `${s.sDay}–${s.eDay} ${monthLabels[month].slice(0, 3)}`
                    return (
                      <div
                        key={i}
                        title={`${s.empleado_nombre} · ${t.label} · ${range}${pendingFlag ? ' · pendiente' : ''}`}
                        className="absolute top-1.5 bottom-1.5 rounded-md flex items-center px-2 gap-1.5 text-[10.5px] font-medium overflow-hidden whitespace-nowrap"
                        style={{
                          left: `calc(${left}% + 2px)`,
                          width: `calc(${width}% - 4px)`,
                          background: pendingFlag ? 'transparent' : `${t.color}1F`,
                          border: pendingFlag ? `1px dashed ${t.color}88` : `1px solid ${t.color}33`,
                          color: t.color,
                        }}
                      >
                        {width > 6 && <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: t.color }} />}
                        {width > 8 && <span className="truncate">{t.label}{pendingFlag ? ' · pendiente' : ''}</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="px-5 py-3 border-t border-[var(--line)] flex items-center justify-between text-[11px] text-[var(--ink-3)] flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          {TIPOS_KEYS.map((k) => {
            const v = TIPO_TONE[k]
            return (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-sm"
                  style={{ background: `${v.color}40`, border: `1px solid ${v.color}66` }}
                />
                {v.label}
              </span>
            )
          })}
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-sm border border-dashed"
              style={{ borderColor: 'var(--ink-3)' }}
            />
            Pendiente
          </span>
        </div>
        <span className="font-mono">
          {empleados.length} {empleados.length === 1 ? 'empleado' : 'empleados'} · {monthLabels[month]} {year}
        </span>
      </div>
    </div>
  )
}

export default CalendarioVacacionesV2
